import {
  FormWorkflowSettings,
  FormWorkflowStatus,
  FormWorkflowTransition,
} from 'src/shared/store/projects/types';
import { userRolesEnum } from 'src/shared/store/user/constants';

export type WorkflowRoleOption = {
  value: string;
  label: string;
};

export const workflowRoleOptions: WorkflowRoleOption[] = [
  { value: userRolesEnum.ADMIN, label: 'Администратор' },
  { value: userRolesEnum.DEVELOPER, label: 'Разработчик формы' },
  { value: userRolesEnum.OPERATOR, label: 'Оператор ввода' },
  { value: userRolesEnum.VIEWER, label: 'Пользователь просмотра' },
];

const roleValues = new Set(workflowRoleOptions.map(option => option.value));

const normalizeRole = (value?: string): string => {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'form_developer') {
    return userRolesEnum.DEVELOPER;
  }
  return roleValues.has(normalized) ? normalized : '';
};

export const normalizeWorkflowStatusCode = (value?: string): string =>
  (value || '').trim().toLowerCase();

const normalizeRoles = (roles?: string[]): string[] => {
  const result: string[] = [];

  (roles || []).forEach(role => {
    const normalized = normalizeRole(role);
    if (!normalized || result.includes(normalized)) {
      return;
    }
    result.push(normalized);
  });

  return result;
};

const normalizeUserIds = (userIds?: number[]): number[] => {
  const result: number[] = [];

  (userIds || []).forEach(userId => {
    const normalized = Number(userId);
    if (!Number.isFinite(normalized) || normalized <= 0) {
      return;
    }
    const normalizedId = Math.trunc(normalized);
    if (result.includes(normalizedId)) {
      return;
    }
    result.push(normalizedId);
  });

  return result;
};

const hasStatusContent = (status: Partial<FormWorkflowStatus>): boolean =>
  Boolean(
    (status.code || '').trim() ||
      (status.name || '').trim() ||
      status.readOnly ||
      status.readRoles?.length ||
      status.responsibleRoles?.length ||
      status.responsibleUserIds?.length,
  );

const hasTransitionContent = (transition: Partial<FormWorkflowTransition>): boolean =>
  Boolean(
    (transition.from || '').trim() ||
      (transition.to || '').trim() ||
      transition.allowedRoles?.length ||
      transition.allowedUserIds?.length,
  );

export const buildLinearWorkflowTransitions = (
  statuses: FormWorkflowStatus[],
): FormWorkflowTransition[] => {
  if (statuses.length < 2) {
    return [];
  }

  return statuses.slice(0, -1).map((status, index) => ({
    from: normalizeWorkflowStatusCode(status.code),
    to: normalizeWorkflowStatusCode(statuses[index + 1].code),
    allowedRoles: [],
    allowedUserIds: [],
  }));
};

export const buildDefaultWorkflowSettings = (): FormWorkflowSettings => ({
  enabled: true,
  initialStatus: 'draft',
  manualTransitionEnabled: true,
  requireCommentOnTransition: false,
  statuses: [
    {
      code: 'draft',
      name: 'Черновик',
      order: 1,
      readOnly: false,
      readRoles: [userRolesEnum.ADMIN, userRolesEnum.DEVELOPER, userRolesEnum.OPERATOR],
      responsibleRoles: [userRolesEnum.OPERATOR],
      responsibleUserIds: [],
    },
    {
      code: 'review',
      name: 'Проверка',
      order: 2,
      readOnly: false,
      readRoles: [userRolesEnum.ADMIN, userRolesEnum.DEVELOPER],
      responsibleRoles: [userRolesEnum.DEVELOPER],
      responsibleUserIds: [],
    },
    {
      code: 'approved',
      name: 'Утверждено',
      order: 3,
      readOnly: false,
      readRoles: [
        userRolesEnum.ADMIN,
        userRolesEnum.DEVELOPER,
        userRolesEnum.OPERATOR,
        userRolesEnum.VIEWER,
      ],
      responsibleRoles: [userRolesEnum.VIEWER],
      responsibleUserIds: [],
    },
    {
      code: 'rejected',
      name: 'Отклонено',
      order: 4,
      readOnly: false,
      readRoles: [userRolesEnum.ADMIN, userRolesEnum.DEVELOPER, userRolesEnum.OPERATOR],
      responsibleRoles: [userRolesEnum.OPERATOR],
      responsibleUserIds: [],
    },
  ],
  transitions: [
    {
      from: 'draft',
      to: 'review',
      allowedRoles: [userRolesEnum.OPERATOR, userRolesEnum.DEVELOPER],
      allowedUserIds: [],
    },
    {
      from: 'review',
      to: 'approved',
      allowedRoles: [userRolesEnum.DEVELOPER, userRolesEnum.ADMIN],
      allowedUserIds: [],
    },
    {
      from: 'review',
      to: 'rejected',
      allowedRoles: [userRolesEnum.DEVELOPER, userRolesEnum.ADMIN],
      allowedUserIds: [],
    },
    {
      from: 'rejected',
      to: 'draft',
      allowedRoles: [userRolesEnum.OPERATOR, userRolesEnum.DEVELOPER],
      allowedUserIds: [],
    },
  ],
});

export const buildEmptyWorkflowSettings = (): FormWorkflowSettings => ({
  enabled: false,
  initialStatus: '',
  manualTransitionEnabled: true,
  requireCommentOnTransition: false,
  statuses: [],
  transitions: [],
});

export const normalizeWorkflowSettingsForSave = (
  input?: FormWorkflowSettings,
): FormWorkflowSettings => {
  const base = input || buildEmptyWorkflowSettings();

  const statuses = (base.statuses || []).filter(hasStatusContent).map((status, index) => {
    const code = normalizeWorkflowStatusCode(status.code);
    const name = (status.name || '').trim() || code;

    return {
      code,
      name,
      order: index + 1,
      readOnly: Boolean(status.readOnly),
      readRoles: normalizeRoles(status.readRoles),
      responsibleRoles: normalizeRoles(status.responsibleRoles),
      responsibleUserIds: normalizeUserIds(status.responsibleUserIds),
    };
  });

  const transitions = (base.transitions || []).filter(hasTransitionContent).map(transition => ({
    from: normalizeWorkflowStatusCode(transition.from),
    to: normalizeWorkflowStatusCode(transition.to),
    allowedRoles: normalizeRoles(transition.allowedRoles),
    allowedUserIds: normalizeUserIds(transition.allowedUserIds),
  }));

  const initialStatus = normalizeWorkflowStatusCode(base.initialStatus) || statuses[0]?.code || '';

  return {
    enabled: Boolean(base.enabled),
    initialStatus,
    manualTransitionEnabled: base.manualTransitionEnabled !== false,
    requireCommentOnTransition: Boolean(base.requireCommentOnTransition),
    statuses,
    transitions,
  };
};

export const validateWorkflowSettings = (input?: FormWorkflowSettings): string[] => {
  const settings = normalizeWorkflowSettingsForSave(input);

  if (!settings.enabled) {
    return [];
  }

  const errors: string[] = [];
  const statusCodes = new Set<string>();

  if (!settings.statuses?.length) {
    errors.push('Добавьте хотя бы один статус бизнес-процесса');
    return errors;
  }

  settings.statuses.forEach((status, index) => {
    const code = normalizeWorkflowStatusCode(status.code);
    const label = status.name || `Статус #${index + 1}`;

    if (!code) {
      errors.push(`Статус #${index + 1}: требуется код`);
      return;
    }

    if (!/^[a-z0-9_]+$/.test(code)) {
      errors.push(`${label}: код должен содержать только a-z, 0-9 и _`);
    }

    if (statusCodes.has(code)) {
      errors.push(`${label}: код статуса дублируется`);
    } else {
      statusCodes.add(code);
    }

    if (!status.responsibleRoles?.length && !status.responsibleUserIds?.length) {
      errors.push(`${label}: назначьте хотя бы одну ответственную роль или пользователя`);
    }
  });

  if (!settings.initialStatus) {
    errors.push('Выберите начальный статус');
  } else if (!statusCodes.has(settings.initialStatus)) {
    errors.push('Начальный статус должен ссылаться на существующий статус');
  }

  settings.transitions?.forEach((transition, index) => {
    const title = `Переход #${index + 1}`;
    if (!transition.from || !transition.to) {
      errors.push(`${title}: заполните статусы "из" и "в"`);
      return;
    }
    if (transition.from === transition.to) {
      errors.push(`${title}: статус "из" и "в" не должны совпадать`);
    }
    if (!statusCodes.has(transition.from)) {
      errors.push(`${title}: исходный статус не найден в модели`);
    }
    if (!statusCodes.has(transition.to)) {
      errors.push(`${title}: целевой статус не найден в модели`);
    }
  });

  return errors;
};
